/**
 * Page admin protégée par mot de passe unique.
 *
 * Architecture :
 * - Token JWT stocké en localStorage (clé `signapharma_admin_token`)
 * - Si pas de token / token invalide → affiche le formulaire de login
 * - Sinon → affiche le dashboard avec onglets (Stats, Commandes, Devis, Contacts, Captures)
 * - Toutes les requêtes envoient `Authorization: Bearer <token>`
 *
 * Sécurité :
 * - Hors layout principal (pas de Header/Footer) pour design dédié
 * - Auto-logout si l'API renvoie 401 (token expiré)
 */
import React, { useEffect, useState, useCallback, useMemo } from "react";
import axios from "axios";
import {
  Lock,
  LogOut,
  RefreshCw,
  FileDown,
  Mail,
  TrendingUp,
  Package,
  MessageSquare,
  Users,
  ShoppingCart,
  Loader2,
  AlertTriangle,
} from "lucide-react";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api/admin`;
const TOKEN_KEY = "signapharma_admin_token";

// ─────────────────────────────────────────────────────────────────────────────
// Helpers
// ─────────────────────────────────────────────────────────────────────────────
const fmtCHF = (n) =>
  new Intl.NumberFormat("fr-FR", { maximumFractionDigits: 0 }).format(
    Math.round(Number(n) || 0),
  ) + " €";

const fmtDate = (iso) => {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleDateString("fr-FR", {
      day: "2-digit",
      month: "2-digit",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  } catch {
    return String(iso).slice(0, 16);
  }
};

const adminAxios = axios.create({ baseURL: API });
adminAxios.interceptors.request.use((config) => {
  const t = localStorage.getItem(TOKEN_KEY);
  if (t) config.headers.Authorization = `Bearer ${t}`;
  return config;
});

// ─────────────────────────────────────────────────────────────────────────────
// Login form
// ─────────────────────────────────────────────────────────────────────────────
function LoginForm({ onSuccess }) {
  const [password, setPassword] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");

  const submit = async (e) => {
    e.preventDefault();
    if (submitting) return;
    setSubmitting(true);
    setError("");
    try {
      const { data } = await adminAxios.post("/login", { password });
      localStorage.setItem(TOKEN_KEY, data.token);
      onSuccess();
    } catch (e) {
      const msg = e.response?.data?.detail || e.message || "Erreur";
      setError(typeof msg === "string" ? msg : "Erreur");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-[#0a0a0a] p-6">
      <div className="w-full max-w-md">
        <div className="text-center mb-10">
          <div className="inline-flex items-center justify-center w-16 h-16 border-2 border-[#39FF14] mb-6">
            <Lock size={28} className="text-[#39FF14]" />
          </div>
          <h1 className="font-display text-4xl text-white tracking-tight">
            Admin <span className="text-[#39FF14]">Signapharma</span>
          </h1>
          <p className="text-zinc-500 font-mono text-[11px] uppercase tracking-widest mt-2">
            Accès réservé · Authentification requise
          </p>
        </div>
        <form onSubmit={submit} className="space-y-4" data-testid="admin-login-form">
          <div>
            <label className="block text-xs font-mono uppercase tracking-widest text-zinc-400 mb-2">
              Mot de passe
            </label>
            <input
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              autoFocus
              required
              className="w-full bg-[#080808] border border-white/10 px-4 py-3 text-white font-mono focus:outline-none focus:border-[#39FF14] transition-colors"
              data-testid="admin-login-password"
            />
          </div>
          {error && (
            <div
              className="border border-red-500/40 bg-red-500/10 p-3 flex items-start gap-2 text-red-300 text-sm font-mono"
              data-testid="admin-login-error"
            >
              <AlertTriangle size={16} className="shrink-0 mt-0.5" />
              {error}
            </div>
          )}
          <button
            type="submit"
            disabled={submitting || !password}
            className="btn-neon w-full justify-center disabled:opacity-50"
            data-testid="admin-login-submit"
          >
            {submitting ? <Loader2 size={16} className="animate-spin" /> : <Lock size={16} />}
            {submitting ? "Connexion…" : "Se connecter"}
          </button>
        </form>
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// Stat tile
// ─────────────────────────────────────────────────────────────────────────────
function StatTile({ icon: Icon, label, value, hint }) {
  return (
    <div className="bg-[#0a0a0a] border border-white/10 p-5 flex flex-col gap-2 hover:border-[#39FF14]/40 transition-colors">
      <div className="flex items-center gap-2 text-zinc-500">
        <Icon size={14} />
        <span className="font-mono text-[10px] uppercase tracking-widest">{label}</span>
      </div>
      <div className="font-display text-3xl text-white">{value}</div>
      {hint && (
        <div className="font-mono text-[10px] text-zinc-600 uppercase tracking-widest">
          {hint}
        </div>
      )}
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// Orders table
// ─────────────────────────────────────────────────────────────────────────────
function orderMatchesQuery(order, query) {
  if (!query) return true;
  const q = query.toLowerCase().trim();
  if (!q) return true;
  // Recherche large : session_id, email, nom client, ID de sous-item de croix,
  // slug produit, désignId de sous-item.
  const hay = [
    order.session_id || "",
    order.customer_email || "",
    order.metadata?.customer_name || "",
  ];
  for (const it of order.items || []) {
    hay.push(it.product_slug || "");
    const cfg = it.configuration || {};
    hay.push(cfg.activeId || "");
    for (const sub of cfg.items || []) {
      hay.push(sub.id || "");
      hay.push(sub.designId || "");
      hay.push(sub.family || "");
    }
  }
  return hay.join(" ").toLowerCase().includes(q);
}

function OrderItemRow({ item }) {
  const cfg = item.configuration || {};
  const previewImg = cfg.previewImage;
  const subItems = Array.isArray(cfg.items) ? cfg.items : [];
  const productName = typeof item.product_name === "object"
    ? (item.product_name?.fr || item.product_slug)
    : (item.product_name || item.product_slug);
  return (
    <div className="flex gap-3 py-2 border-b border-white/5 last:border-b-0">
      {previewImg ? (
        <img
          src={previewImg}
          alt=""
          className="w-16 h-16 shrink-0 object-contain bg-black border border-[#39FF14]/40"
        />
      ) : (
        <div className="w-16 h-16 shrink-0 bg-[#0a0a0a] border border-white/10 flex items-center justify-center text-[9px] text-zinc-600 font-mono uppercase">
          Pas d&apos;aperçu
        </div>
      )}
      <div className="flex-1 min-w-0 text-xs">
        <div className="text-white font-display font-semibold">{productName}</div>
        <div className="text-zinc-500 font-mono text-[10px]">
          {item.product_slug} · qty {item.quantity || 1}
        </div>
        {subItems.length > 0 && (
          <ul className="mt-1 space-y-0.5">
            {subItems.map((s, i) => (
              <li key={s.id || i} className="text-zinc-400 font-mono text-[10px] truncate">
                <span className="text-[#39FF14]">›</span>{" "}
                <span className="text-zinc-500">{s.id}</span>{" "}
                — {s.family || "?"} · {s.designId || "?"} · {s.sizeCm || "?"}cm · LED {s.colorId || "?"} · {s.face || "?"}
              </li>
            ))}
          </ul>
        )}
      </div>
    </div>
  );
}

function OrdersTable({ orders, onRefresh }) {
  const [busy, setBusy] = useState({});
  const [query, setQuery] = useState("");
  const [expanded, setExpanded] = useState({});

  const handleDownload = async (sessionId) => {
    setBusy((b) => ({ ...b, [sessionId]: "pdf" }));
    try {
      const resp = await adminAxios.get(`/order/${sessionId}/pdf`, { responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([resp.data], { type: "application/pdf" }));
      const a = document.createElement("a");
      a.href = url;
      a.download = `signapharma_${sessionId.slice(0, 16)}.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
    } catch (e) {
      toast.error("Téléchargement échoué");
    } finally {
      setBusy((b) => ({ ...b, [sessionId]: null }));
    }
  };

  const handleResend = async (sessionId) => {
    setBusy((b) => ({ ...b, [sessionId]: "mail" }));
    try {
      const { data } = await adminAxios.post(`/order/${sessionId}/resend-email`);
      toast.success(data.sent ? "Email renvoyé" : "Email envoyé en fallback");
    } catch {
      toast.error("Envoi échoué");
    } finally {
      setBusy((b) => ({ ...b, [sessionId]: null }));
    }
  };

  if (!orders.length) {
    return (
      <div className="text-zinc-500 font-mono text-sm text-center py-12">
        Aucune commande payée pour l&apos;instant.
      </div>
    );
  }
  const filtered = orders.filter((o) => orderMatchesQuery(o, query));
  return (
    <div className="space-y-4">
      {/* Barre de recherche */}
      <div className="flex flex-col sm:flex-row sm:items-center gap-3">
        <input
          type="text"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          placeholder="Rechercher : ID d'élément (ex: 1785066021630, ezlly), email, session…"
          className="flex-1 bg-[#0a0a0a] border border-white/10 focus:border-[#39FF14] px-4 py-2 text-sm text-white placeholder:text-zinc-600 font-mono outline-none transition-colors"
          data-testid="admin-orders-search"
        />
        <span className="text-xs font-mono text-zinc-500 whitespace-nowrap">
          {filtered.length}/{orders.length} commande{filtered.length > 1 ? "s" : ""}
        </span>
      </div>
      {filtered.length === 0 && query && (
        <div className="text-zinc-500 font-mono text-sm text-center py-8 border border-white/5 bg-[#080808]">
          Aucun résultat pour « {query} ». L&apos;ID recherché n&apos;existe peut-être pas dans les commandes payées.
        </div>
      )}
      <div className="overflow-x-auto">
        <table className="w-full text-sm" data-testid="admin-orders-table">
        <thead className="text-zinc-500 font-mono text-[10px] uppercase tracking-widest border-b border-white/10">
          <tr>
            <th className="text-left py-3 px-2">Date</th>
            <th className="text-left py-3 px-2">Client</th>
            <th className="text-left py-3 px-2">Email</th>
            <th className="text-right py-3 px-2">Montant</th>
            <th className="text-left py-3 px-2">Articles</th>
            <th className="text-right py-3 px-2">Actions</th>
          </tr>
        </thead>
        <tbody className="text-zinc-300">
          {filtered.map((o) => (
            <React.Fragment key={o.session_id}>
            <tr
              className="border-b border-white/5 hover:bg-white/[0.02] cursor-pointer"
              data-testid={`admin-order-${o.session_id}`}
              onClick={() => setExpanded((e) => ({ ...e, [o.session_id]: !e[o.session_id] }))}
            >
              <td className="py-3 px-2 font-mono text-xs text-zinc-400">
                {fmtDate(o.paid_at || o.created_at)}
              </td>
              <td className="py-3 px-2 text-white">
                {o.metadata?.customer_name || "—"}
              </td>
              <td className="py-3 px-2 text-zinc-400 text-xs">
                {o.customer_email || "—"}
              </td>
              <td className="py-3 px-2 text-right text-[#39FF14] font-mono">
                {fmtCHF(o.amount_chf)}
              </td>
              <td className="py-3 px-2 text-zinc-400 font-mono text-xs">
                {(o.items || []).length} article{(o.items || []).length > 1 ? "s" : ""}
                <span className="ml-2 text-[#39FF14]">{expanded[o.session_id] ? "▼" : "▶"}</span>
              </td>
              <td className="py-3 px-2 text-right" onClick={(e) => e.stopPropagation()}>
                <div className="inline-flex gap-2">
                  <button
                    onClick={() => handleDownload(o.session_id)}
                    disabled={busy[o.session_id]}
                    className="inline-flex items-center gap-1 border border-white/10 hover:border-[#39FF14] hover:text-[#39FF14] px-2 py-1 transition-colors text-xs font-mono uppercase tracking-widest text-zinc-400 disabled:opacity-50"
                    title="Télécharger fiche PDF"
                    data-testid={`admin-order-pdf-${o.session_id}`}
                  >
                    {busy[o.session_id] === "pdf" ? (
                      <Loader2 size={12} className="animate-spin" />
                    ) : (
                      <FileDown size={12} />
                    )}
                    PDF
                  </button>
                  <button
                    onClick={() => handleResend(o.session_id)}
                    disabled={busy[o.session_id]}
                    className="inline-flex items-center gap-1 border border-white/10 hover:border-[#39FF14] hover:text-[#39FF14] px-2 py-1 transition-colors text-xs font-mono uppercase tracking-widest text-zinc-400 disabled:opacity-50"
                    title="Renvoyer l'email"
                    data-testid={`admin-order-resend-${o.session_id}`}
                  >
                    {busy[o.session_id] === "mail" ? (
                      <Loader2 size={12} className="animate-spin" />
                    ) : (
                      <Mail size={12} />
                    )}
                    Email
                  </button>
                </div>
              </td>
            </tr>
            {expanded[o.session_id] && (
              <tr data-testid={`admin-order-detail-${o.session_id}`}>
                <td colSpan={6} className="bg-[#050505] px-4 py-3 border-b border-[#39FF14]/20">
                  <div className="text-[10px] font-mono text-zinc-500 uppercase tracking-widest mb-2">
                    Session : <span className="text-zinc-300">{o.session_id}</span>
                  </div>
                  <div>
                    {(o.items || []).map((it, idx) => (
                      <OrderItemRow key={idx} item={it} />
                    ))}
                  </div>
                </td>
              </tr>
            )}
            </React.Fragment>
          ))}
        </tbody>
        </table>
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// Generic list (quotes, contacts, captures)
// ─────────────────────────────────────────────────────────────────────────────
function GenericList({ items, fields, emptyMsg }) {
  if (!items.length) {
    return (
      <div className="text-zinc-500 font-mono text-sm text-center py-12">{emptyMsg}</div>
    );
  }
  return (
    <div className="space-y-2">
      {items.map((it, i) => (
        <div
          key={it.id || it._id || `${it.email || ""}-${i}`}
          className="bg-[#0a0a0a] border border-white/10 p-4 grid grid-cols-1 md:grid-cols-12 gap-3"
        >
          {fields.map((f) => (
            <div key={f.key} className={f.cls || "md:col-span-3"}>
              <div className="font-mono text-[9px] uppercase tracking-widest text-zinc-500 mb-1">
                {f.label}
              </div>
              <div className={f.valueCls || "text-sm text-white break-all"}>
                {f.render ? f.render(it) : it[f.key] || "—"}
              </div>
            </div>
          ))}
        </div>
      ))}
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// Dashboard
// ─────────────────────────────────────────────────────────────────────────────
const TABS = [
  { id: "stats", label: "Statistiques", icon: TrendingUp },
  { id: "orders", label: "Commandes", icon: ShoppingCart },
  { id: "quotes", label: "Devis", icon: Package },
  { id: "contacts", label: "Contacts", icon: MessageSquare },
  { id: "captures", label: "Emails capturés", icon: Users },
];

function Dashboard({ onLogout }) {
  const [tab, setTab] = useState("stats");
  const [stats, setStats] = useState(null);
  const [orders, setOrders] = useState([]);
  const [quotes, setQuotes] = useState([]);
  const [contacts, setContacts] = useState([]);
  const [captures, setCaptures] = useState([]);
  const [loading, setLoading] = useState(false);
  const [lastRefresh, setLastRefresh] = useState(null);

  const handle401 = useCallback(() => {
    localStorage.removeItem(TOKEN_KEY);
    onLogout();
  }, [onLogout]);

  const fetchAll = useCallback(async () => {
    setLoading(true);
    try {
      const [s, o, q, c, e] = await Promise.all([
        adminAxios.get("/stats").then((r) => r.data),
        adminAxios.get("/orders").then((r) => r.data),
        adminAxios.get("/quotes").then((r) => r.data),
        adminAxios.get("/contacts").then((r) => r.data),
        adminAxios.get("/email-captures").then((r) => r.data),
      ]);
      setStats(s);
      setOrders(o);
      setQuotes(q);
      setContacts(c);
      setCaptures(e);
      setLastRefresh(new Date());
    } catch (err) {
      if (err.response?.status === 401) handle401();
      else toast.error("Erreur de chargement");
    } finally {
      setLoading(false);
    }
  }, [handle401]);

  useEffect(() => {
    fetchAll();
  }, [fetchAll]);

  const quoteFields = useMemo(
    () => [
      { key: "created_at", label: "Date", cls: "md:col-span-2", render: (it) => fmtDate(it.created_at) },
      { key: "name", label: "Nom", cls: "md:col-span-2" },
      { key: "email", label: "Email", cls: "md:col-span-2" },
      { key: "phone", label: "Téléphone", cls: "md:col-span-2" },
      { key: "product", label: "Produit", cls: "md:col-span-2" },
      {
        key: "configuration",
        label: "Config",
        cls: "md:col-span-2",
        render: (it) =>
          it.configuration
            ? Object.keys(it.configuration).slice(0, 3).join(", ")
            : "—",
      },
    ],
    [],
  );

  const contactFields = useMemo(
    () => [
      { key: "created_at", label: "Date", cls: "md:col-span-2", render: (it) => fmtDate(it.created_at) },
      { key: "name", label: "Nom", cls: "md:col-span-2" },
      { key: "email", label: "Email", cls: "md:col-span-2" },
      { key: "phone", label: "Téléphone", cls: "md:col-span-2" },
      { key: "message", label: "Message", cls: "md:col-span-4" },
    ],
    [],
  );

  const captureFields = useMemo(
    () => [
      { key: "created_at", label: "Date", cls: "md:col-span-3", render: (it) => fmtDate(it.created_at) },
      { key: "email", label: "Email", cls: "md:col-span-5" },
      { key: "page", label: "Page", cls: "md:col-span-4" },
    ],
    [],
  );

  return (
    <div className="min-h-screen bg-[#0a0a0a]">
      {/* Header */}
      <header className="border-b border-white/10 bg-[#080808] sticky top-0 z-10">
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
          <div>
            <h1 className="font-display text-2xl text-white">
              <span className="text-[#39FF14]">Signapharma</span> Admin
            </h1>
            <p className="text-zinc-500 font-mono text-[10px] uppercase tracking-widest mt-1">
              {lastRefresh ? `Dernière maj : ${fmtDate(lastRefresh.toISOString())}` : "Chargement…"}
            </p>
          </div>
          <div className="flex gap-2">
            <button
              onClick={fetchAll}
              disabled={loading}
              className="inline-flex items-center gap-2 border border-white/10 hover:border-[#39FF14] hover:text-[#39FF14] px-4 py-2 transition-colors text-xs font-mono uppercase tracking-widest text-zinc-300 disabled:opacity-50"
              data-testid="admin-refresh-button"
            >
              {loading ? <Loader2 size={14} className="animate-spin" /> : <RefreshCw size={14} />}
              Rafraîchir
            </button>
            <button
              onClick={handle401}
              className="inline-flex items-center gap-2 border border-red-500/40 hover:bg-red-500/10 text-red-300 px-4 py-2 transition-colors text-xs font-mono uppercase tracking-widest"
              data-testid="admin-logout-button"
            >
              <LogOut size={14} />
              Déconnexion
            </button>
          </div>
        </div>
        {/* Tabs */}
        <div className="max-w-7xl mx-auto px-6">
          <div className="flex gap-1 overflow-x-auto">
            {TABS.map((t) => (
              <button
                key={t.id}
                onClick={() => setTab(t.id)}
                className={`flex items-center gap-2 px-4 py-3 border-b-2 font-mono text-[11px] uppercase tracking-widest whitespace-nowrap transition-colors ${
                  tab === t.id
                    ? "border-[#39FF14] text-[#39FF14]"
                    : "border-transparent text-zinc-500 hover:text-zinc-300"
                }`}
                data-testid={`admin-tab-${t.id}`}
              >
                <t.icon size={14} />
                {t.label}
              </button>
            ))}
          </div>
        </div>
      </header>

      {/* Content */}
      <main className="max-w-7xl mx-auto px-6 py-8">
        {tab === "stats" && (
          <div data-testid="admin-stats-panel">
            {stats ? (
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                <StatTile
                  icon={TrendingUp}
                  label="CA total"
                  value={fmtCHF(stats.revenue_total_chf)}
                  hint={`${stats.orders_paid} commandes payées`}
                />
                <StatTile
                  icon={ShoppingCart}
                  label="CA 30 derniers jours"
                  value={fmtCHF(stats.revenue_30d_chf)}
                  hint={`${stats.orders_30d} commandes`}
                />
                <StatTile
                  icon={Package}
                  label="Panier moyen"
                  value={fmtCHF(stats.avg_basket_chf)}
                  hint="sur commandes payées"
                />
                <StatTile
                  icon={Users}
                  label="Emails capturés"
                  value={stats.email_captures}
                  hint={`${stats.quotes} devis · ${stats.contacts} contacts`}
                />
              </div>
            ) : (
              <div className="text-zinc-500 font-mono text-sm">Chargement…</div>
            )}
          </div>
        )}
        {tab === "orders" && (
          <div data-testid="admin-orders-panel">
            <OrdersTable orders={orders} onRefresh={fetchAll} />
          </div>
        )}
        {tab === "quotes" && (
          <div data-testid="admin-quotes-panel">
            <GenericList
              items={quotes}
              fields={quoteFields}
              emptyMsg="Aucun devis pour l'instant."
            />
          </div>
        )}
        {tab === "contacts" && (
          <div data-testid="admin-contacts-panel">
            <GenericList
              items={contacts}
              fields={contactFields}
              emptyMsg="Aucun message de contact."
            />
          </div>
        )}
        {tab === "captures" && (
          <div data-testid="admin-captures-panel">
            <GenericList
              items={captures}
              fields={captureFields}
              emptyMsg="Aucun email capturé pour l'instant."
            />
          </div>
        )}
      </main>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// Root
// ─────────────────────────────────────────────────────────────────────────────
export default function Admin() {
  const [authed, setAuthed] = useState(null); // null = checking, true/false

  useEffect(() => {
    const t = localStorage.getItem(TOKEN_KEY);
    if (!t) {
      setAuthed(false);
      return;
    }
    adminAxios
      .get("/me")
      .then(() => setAuthed(true))
      .catch(() => {
        localStorage.removeItem(TOKEN_KEY);
        setAuthed(false);
      });
  }, []);

  if (authed === null) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#0a0a0a]">
        <Loader2 size={32} className="text-[#39FF14] animate-spin" />
      </div>
    );
  }
  if (!authed) return <LoginForm onSuccess={() => setAuthed(true)} />;
  return <Dashboard onLogout={() => setAuthed(false)} />;
}
